import i18n from 'i18next';
import { intlTag } from '@/i18n';

const tag = () => intlTag(i18n.language || 'en');

const pad = (n: number) => String(n).padStart(2, '0');

function toDate(value: string): Date {
  return value.length === 10 ? new Date(`${value}T00:00:00`) : new Date(value);
}

/** dd/MM/yyyy in every language (hospital standard). */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** dd/MM/yyyy HH:mm (24-hour). */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${formatDate(value)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** dd/MM/yyyy HH:mm:ss, e.g. for print time stamps. */
export function formatDateTimeSeconds(value: string | Date = new Date()): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** "23/06/1943" -> "1943-06-23" (null when not a real date). */
export function parseDisplayDate(text: string): string | null {
  const m = text.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null;
  return `${y}-${pad(mo)}-${pad(d)}`;
}

/** "1943-06-23" -> "23/06/1943". */
export function isoToDisplay(iso: string | null | undefined): string {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return '';
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
}

export function formatTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatNumber(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(tag(), { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
}

export function formatMoney(value: number | null | undefined, currency = 'LAK'): string {
  if (value === null || value === undefined) return '—';
  try {
    return new Intl.NumberFormat(tag(), {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'LAK' || currency === 'VND' ? 0 : 2,
    }).format(value);
  } catch {
    return `${formatNumber(value, 2)} ${currency}`;
  }
}

/** Short currency for headline figures, e.g. "LAK 637.2M". */
export function formatMoneyCompact(value: number | null | undefined, currency = 'LAK'): string {
  if (value === null || value === undefined) return '—';
  const n = new Intl.NumberFormat(tag(), { notation: 'compact', maximumFractionDigits: 1 }).format(value);
  return `${currency} ${n}`;
}

export function relativeTime(value: string): string {
  const diff = (Date.now() - new Date(value).getTime()) / 1000;
  if (diff < 60) return i18n.t('time.justNow');
  if (diff < 3600) return i18n.t('time.minutesAgo', { count: Math.floor(diff / 60) });
  if (diff < 86400) return i18n.t('time.hoursAgo', { count: Math.floor(diff / 3600) });
  return i18n.t('time.daysAgo', { count: Math.floor(diff / 86400) });
}

/** yyyy-mm-dd for <input type="date"> in local time. */
export function isoDate(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Value for <input type="datetime-local"> in local time. */
export function localDateTimeInput(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${isoDate(d)}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** datetime-local value -> ISO-8601 with the browser's offset. */
export function toIsoWithOffset(local: string): string {
  return new Date(local).toISOString();
}

// Titles the server puts before patient names (Lao for Lao-script names, otherwise English).
const NAME_TITLES = new Set(['ທ້າວ', 'ນາງ', 'ນາງສາວ', 'ເດັກຊາຍ', 'ເດັກຍິງ', 'ດຣ.', 'ພຣະ', 'ສາມະເນນ', 'ແມ່ຂາວ',
  'Mr', 'Mrs', 'Miss', 'Master', 'Dr', 'Ven.', 'Novice', 'Sister']);

/** The words of a name without a leading title, e.g. for initials. */
export function nameParts(name: string): string[] {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 && NAME_TITLES.has(parts[0]) ? parts.slice(1) : parts;
}

/** Hospital (or tenant) name in the UI language: the Lao name for Lao, else the English name when there is one. */
export function hospitalName(h: { name?: string | null; nameEn?: string | null } | null | undefined, lang: string | undefined): string {
  if (!h) return '';
  return (lang?.startsWith('lo') ? h.name : h.nameEn || h.name) ?? '';
}
